export interface HasDisplayOrder {
    id: string;
    sectionId: string | null;
    displayOrder: number | null | undefined;
}

export function resolveDisplayOrderDuplicates<T extends HasDisplayOrder>(
    rows: T[]
): T[] {
    const bySectionMap = new Map<string | null, T[]>();
    for (const row of rows) {
        const key = row.sectionId ?? null;
        if (!bySectionMap.has(key)) bySectionMap.set(key, []);
        bySectionMap.get(key)!.push({ ...row });
    }

    const resolved = new Map<string, T>();
    bySectionMap.forEach((sectionRows) => {
        const orderedRows = sectionRows.filter(
            (row) => typeof row.displayOrder === 'number' && row.displayOrder >= 1
        );
        orderedRows.sort(
            (a, b) =>
                (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
                String(a.id).localeCompare(String(b.id))
        );
        let prev: number | null = null;
        for (const row of orderedRows) {
            if (prev !== null && (row.displayOrder ?? 0) <= prev) {
                row.displayOrder = prev + 1;
            }
            prev = row.displayOrder ?? 0;
            resolved.set(row.id, row);
        }
    });

    return rows.map((row) => resolved.get(row.id) ?? row);
}

export function applySparseDisplayOrderInsert<T extends HasDisplayOrder>(
    allRows: T[],
    insertRow: T
): T[] {
    const insertOrderRaw = insertRow?.displayOrder;
    const insertOrder =
        typeof insertOrderRaw === 'number' ? insertOrderRaw : Number(insertOrderRaw);
    if (!Number.isFinite(insertOrder) || insertOrder < 1) {
        return allRows;
    }

    const targetSectionId = insertRow.sectionId ?? null;
    const otherRows = allRows.filter((r) => r.id !== insertRow.id);

    const unaffected = otherRows.filter(
        (r) => (r.sectionId ?? null) !== targetSectionId
    );
    const sectionRows = otherRows
        .filter((r) => (r.sectionId ?? null) === targetSectionId)
        .map((r) => ({ ...r }));

    const fixed = sectionRows.filter((r) => (r.displayOrder ?? 0) < insertOrder);
    const toPlace = sectionRows
        .filter((r) => (r.displayOrder ?? 0) >= insertOrder)
        .sort(
            (a, b) =>
                (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
                String(a.id).localeCompare(String(b.id))
        );

    const used = new Set<number>();
    fixed.forEach((r) => {
        if (typeof r.displayOrder === 'number' && r.displayOrder >= 1) {
            used.add(r.displayOrder);
        }
    });
    used.add(insertOrder);

    const nextFree = (start: number) => {
        let v = start;
        while (used.has(v)) v += 1;
        return v;
    };

    const placed = toPlace.map((r) => {
        const desired =
            typeof r.displayOrder === 'number' ? r.displayOrder : Number(r.displayOrder);
        const start = Number.isFinite(desired) ? Math.max(insertOrder, desired, 1) : insertOrder;
        const order = used.has(start) ? nextFree(start) : start;
        used.add(order);
        return { ...(r as T), displayOrder: order };
    });

    return [
        ...unaffected,
        ...fixed,
        ...placed,
        { ...(insertRow as T), displayOrder: insertOrder },
    ];
}

interface RowWithDisplayOrder {
    displayOrder: number | null | undefined;
}

export function sortRowsByDisplayOrder<T extends RowWithDisplayOrder>(rows: T[]): T[] {
    return [...rows].sort(
        (a, b) =>
            (a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
            (b.displayOrder ?? Number.MAX_SAFE_INTEGER)
    );
}

export function compactDisplayOrderSequence<T extends RowWithDisplayOrder>(
    rows: T[]
): T[] {
    const orderedRows = sortRowsByDisplayOrder(
        rows.filter((row) => typeof row.displayOrder === 'number')
    );
    const sequenceByRow = new Map(
        orderedRows.map((row, index) => [row, index + 1])
    );
    return rows.map((row) => {
        const sequenceOrder = sequenceByRow.get(row);
        if (sequenceOrder == null || row.displayOrder === sequenceOrder) {
            return row;
        }
        return { ...row, displayOrder: sequenceOrder };
    });
}

export function sortRowsByDisplayOrderPlacingOthersLast<
    T extends RowWithDisplayOrder
>(rows: T[], shouldSort: (row: T) => boolean): T[] {
    return [
        ...sortRowsByDisplayOrder(rows.filter(shouldSort)),
        ...rows.filter((row) => !shouldSort(row)),
    ];
}

export function recalculateDisplayOrderOnRowChange<T extends RowWithDisplayOrder>(
    prevRows: T[],
    nextRows: T[],
    getKey: (row: T) => string
): T[] {
    const prevDisplayOrderByKey = new Map<string, number | null | undefined>();
    prevRows.forEach((row) => prevDisplayOrderByKey.set(getKey(row), row.displayOrder));

    const shadowRows: HasDisplayOrder[] = nextRows.map((row) => ({
        id: getKey(row),
        sectionId: null,
        displayOrder: row.displayOrder,
    }));

    const insertShadowRow = shadowRows.find((shadowRow) => {
        if (!prevDisplayOrderByKey.has(shadowRow.id)) {
            return true;
        }
        return shadowRow.displayOrder !== prevDisplayOrderByKey.get(shadowRow.id);
    });

    const hasValidInsert =
        insertShadowRow != null &&
        typeof insertShadowRow.displayOrder === 'number' &&
        insertShadowRow.displayOrder >= 1;

    const withInsertApplied = hasValidInsert
        ? applySparseDisplayOrderInsert(shadowRows, insertShadowRow)
        : shadowRows;

    const resolvedShadowRows = resolveDisplayOrderDuplicates(withInsertApplied);

    const resolvedDisplayOrderByKey = new Map<string, number | null | undefined>();
    resolvedShadowRows.forEach((shadowRow) =>
        resolvedDisplayOrderByKey.set(shadowRow.id, shadowRow.displayOrder)
    );

    let hasChanges = false;
    const result = nextRows.map((row) => {
        const resolvedDisplayOrder = resolvedDisplayOrderByKey.get(getKey(row));
        if (resolvedDisplayOrder === row.displayOrder) {
            return row;
        }
        hasChanges = true;
        return { ...row, displayOrder: resolvedDisplayOrder };
    });

    return hasChanges ? result : nextRows;
}
